import { Dispatch, SetStateAction, useCallback, useMemo } from "react"
import {
  AlertCircle,
  Block,
  LabelLarge,
  LabelXSmall,
  ParagraphSmall,
  useStyletron,
} from "@mezo-org/mezo-clay"

import { RootState, selectToken, useDappSelector } from "#/redux-state"
import { FRIENDLY_DECIMALS_AMOUNT } from "#/shared/constants"
import { useSwapInput, useTokenUsdPrice } from "#/shared/hooks"
import { SwapFormField } from "#/shared/types"
import {
  bigIntToHumanReadableFormat,
  floatToBigInt,
  separateThousandsWithComma,
} from "#/shared/utils"

import SwapModalTokenInput from "./SwapModalTokenInput"

type SwapModalInputProps = {
  label: string
  fieldName: SwapFormField
  onOpenAssetSelector: () => void
  currentField: SwapFormField | null
  setCurrentField: Dispatch<SetStateAction<SwapFormField | null>>
  setIsCalculatingSwapAmounts: Dispatch<SetStateAction<boolean>>
  disabled: boolean
}

export default function SwapModalInput({
  label,
  fieldName,
  onOpenAssetSelector,
  currentField,
  setCurrentField,
  setIsCalculatingSwapAmounts,
  disabled,
}: SwapModalInputProps) {
  const { amount, token, error, onInputChange, onInputBlur } = useSwapInput(
    fieldName,
    currentField,
  )

  const { tokenBalance, tokenDecimals } = useDappSelector((state: RootState) =>
    selectToken(state, token),
  )

  const [css, theme] = useStyletron()

  const { getTokenUsdValueLabel } = useTokenUsdPrice()

  const swapInputUsdValue = useMemo(
    () =>
      getTokenUsdValueLabel(
        floatToBigInt(amount, Number(tokenDecimals)),
        token,
        tokenDecimals,
      ),
    [amount, token, tokenDecimals, getTokenUsdValueLabel],
  )

  const handleBlur = useCallback(async () => {
    // Show loading action until amounts are calculated
    setIsCalculatingSwapAmounts(true)
    await onInputBlur()

    setIsCalculatingSwapAmounts(false)
    setCurrentField(null)
  }, [onInputBlur, setCurrentField, setIsCalculatingSwapAmounts])

  return (
    <Block
      className={css({
        width: "100%",
        padding: theme.sizing.scale600,
        border: `1px solid ${theme.colors.borderOpaque}`,
        borderRadius: theme.borders.radius500,
      })}
    >
      <Block
        className={css({
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
        })}
      >
        <LabelLarge>{label}</LabelLarge>
        <LabelXSmall>
          Balance:{" "}
          <LabelXSmall
            as="span"
            color={error ? theme.colors.negative : theme.colors.black}
          >
            {bigIntToHumanReadableFormat(
              tokenBalance,
              Number(tokenDecimals),
              FRIENDLY_DECIMALS_AMOUNT,
            )}
          </LabelXSmall>
        </LabelXSmall>
      </Block>
      <SwapModalTokenInput
        value={amount}
        token={token}
        hasError={!!error}
        disabled={disabled}
        onInputChange={onInputChange}
        onOpenAssetSelector={onOpenAssetSelector}
        onBlur={handleBlur}
        onFocus={() => setCurrentField(fieldName)}
      />
      {error && (
        <Block
          className={css({
            display: "flex",
            alignItems: "center",
            gap: theme.sizing.scale100,
            marginTop: theme.sizing.scale300,
          })}
        >
          <AlertCircle size="14px" color={theme.colors.contentNegative} />
          <ParagraphSmall color={theme.colors.contentNegative}>
            {error}
          </ParagraphSmall>
        </Block>
      )}
      <LabelXSmall color={theme.colors.contentSecondary}>
        ~ ${separateThousandsWithComma(swapInputUsdValue, 2)}
      </LabelXSmall>
    </Block>
  )
}
